import { useMemo } from 'react';
import { Button } from '@music-vine/cadence/ui';
import { Text } from '@music-vine/cadence/ui';
import { useHasAnyRole } from '@/hooks/auth';
import { formatOverdueDate, parseOverdueDate } from '@/utils/overdue-payment';
import { useEnableAutoRenew } from '@/hooks/billing/useEnableAutoRenew';
import { useSubEndDate } from '@/hooks/billing/useSubEndDate';
import { UpdatePaymentButton } from '@/components/UpdatePaymentButton';
interface PopoverFooterOverdueDescriptionProps {
  channelText: string;
  overdueDate?: string;
}
const PopoverFooterOverdueDescription = ({
  overdueDate,
  channelText
}: PopoverFooterOverdueDescriptionProps) => {
  const defaultDescription = <div className="font-sans">
      There was a payment issue, so videos posted on your {channelText} may not
      be protected.
    </div>;
  if (!overdueDate) {
    return defaultDescription;
  }
  const parsedDate = parseOverdueDate({
    overdueDate
  });
  if (!parsedDate) {
    return defaultDescription;
  }
  const formattedDate = formatOverdueDate({
    overdueDate: parsedDate
  });
  return <div className="font-sans" data-sentry-component="PopoverFooterOverdueDescription" data-sentry-source-file="SafelistPopoverFooter.tsx">
      There was a payment issue, so videos posted{' '}
      <span className="font-semibold">after {formattedDate}</span> aren't
      currently protected.
    </div>;
};
interface SafelistPopoverFooterProps {
  maxSafelistedChannels: number;
  onUpgrade: () => void;
  overdueDate?: string;
  overduePaymentId?: string;
  whitelistedChannels?: unknown[];
}
export const SafelistPopoverFooter = ({
  overduePaymentId,
  overdueDate,
  whitelistedChannels,
  maxSafelistedChannels,
  onUpgrade
}: SafelistPopoverFooterProps) => {
  // Top two tiers have nothing to upgrade to, so they never see the upsell
  // (DEV-6380). Business matches Pro's 10-channel limit.
  const isTopTier = useHasAnyRole(['Pro', 'Business']);
  const subEndDate = useSubEndDate();
  const {
    enableAutoRenew,
    isUpdating
  } = useEnableAutoRenew();
  const channelText = maxSafelistedChannels === 1 ? 'channel' : 'channels';
  const popoverFooter = useMemo(() => {
    if (overduePaymentId) {
      return <>
          <div className="w-full bg-red-50 px-6 py-5 max-md:hidden dark:bg-red-950">
            <div className="flex flex-col gap-4 text-black text-sm dark:text-white">
              <PopoverFooterOverdueDescription channelText={channelText} overdueDate={overdueDate} />
              <div>
                Update your payment method to keep your {channelText}{' '}
                safelisted.
              </div>
              <Button asChild className="w-min rounded-md" fontSize={{
              '2xl': 'sm'
            }} size={{
              default: 'sm',
              '2xl': 'default'
            }} variant="contrast">
                <UpdatePaymentButton>Update payment method</UpdatePaymentButton>
              </Button>
            </div>
          </div>
          <div className="w-full overflow-hidden px-4 pb-12 max-xs:pb-5 md:hidden">
            <div className="w-full rounded-xl bg-red-50 px-6 py-5 dark:bg-red-950">
              <div className="flex items-center justify-between gap-4 font-semibold">
                <div>
                  <p className="text-black text-sm dark:text-white">
                    Your latest payment failed.
                  </p>
                  <p className="text-black text-sm dark:text-white">
                    Resolve it to resume safelisting.
                  </p>
                </div>
                <Button asChild className="w-min rounded-md" fontSize="sm" size="default" variant="error">
                  <UpdatePaymentButton>Fix it</UpdatePaymentButton>
                </Button>
              </div>
            </div>
          </div>
        </>;
    }
    if (subEndDate) {
      return <div className="w-full bg-red-50 px-6 py-5 dark:bg-red-950">
          <div className="flex flex-col items-start gap-4">
            <Text className="text-gray-950 text-sm">
              After <strong className="font-semibold">{subEndDate}</strong>,
              your videos won't be protected from copyright claims without an
              active subscription.
            </Text>
            <Button disabled={isUpdating} onClick={enableAutoRenew} size="sm" variant="contrast">
              {isUpdating ? 'Reactivating...' : 'Reactivate subscription'}
            </Button>
          </div>
        </div>;
    }
    if (whitelistedChannels && whitelistedChannels.length >= maxSafelistedChannels && !isTopTier) {
      return <>
          <div className="w-full bg-gray-50 px-6 py-5 max-md:hidden dark:bg-gray-900">
            <Text className="mb-1 font-semibold text-black text-sm">
              Need more channels?
            </Text>
            <Text className="mb-3 text-black text-xs 2xl:text-sm">
              Upgrade to grow your safelist.
            </Text>
            <Button fontSize={{
            '2xl': 'sm'
          }} onClick={onUpgrade} size={{
            default: 'sm',
            '2xl': 'default'
          }} variant="brand">
              Upgrade
            </Button>
          </div>
          <div className="w-full overflow-hidden px-4 pb-12 max-xs:pb-5 md:hidden">
            <div className="flex w-full items-center justify-between overflow-hidden rounded-xl bg-pink-50 px-6 py-5 dark:bg-pink-950">
              <Text className="font-semibold text-black text-sm">
                Need more channels?
              </Text>
              <Button fontSize="sm" onClick={onUpgrade} size="default" variant="brand">
                Upgrade
              </Button>
            </div>
          </div>
        </>;
    }
    return null;
  }, [overduePaymentId, whitelistedChannels, maxSafelistedChannels, isTopTier, overdueDate, channelText, onUpgrade]);
  return popoverFooter;
};